import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Star, Lock, Repeat } from "lucide-react";
import { track } from "@/lib/analytics";
import { useSeo, SITE } from "@/hooks/useSeo";

const PLANS = [
  {
    id: "maintainer",
    name: "Maintainer",
    monthlyPrice: 10,
    yearlyPrice: 120,
    monthlyHref: "https://buy.stripe.com/cNi28sd3S9Zs7Pl8e22sM04",
    yearlyHref: "https://buy.stripe.com/eVqfZi5Bq2x07PleCq2sM05",
    tagline: "Stay on top of seasonal cleanings without thinking about it.",
    features: [
      "1 professional gutter cleaning per year (spring or fall — you pick)",
      "Annual gutter & downspout inspection with a photo report texted to you",
      "Priority scheduling over non-members during busy season",
      "10% member discount on repairs, guard installs, and add-on work",
      "Locked-in member rate — no seasonal price increases",
    ],
    popular: false,
  },
  {
    id: "protector",
    name: "Protector",
    monthlyPrice: 20,
    yearlyPrice: 240,
    monthlyHref: "https://buy.stripe.com/14AbJ2bZO0oSfhNeCq2sM06",
    yearlyHref: "https://buy.stripe.com/28EeVe7JydbEb1xfGu2sM07",
    tagline: "Spring and fall covered, plus minor repairs handled on the spot.",
    features: [
      "Everything in Maintainer, plus:",
      "2 cleanings per year (spring AND fall)",
      "Free minor repairs each visit — resealing joints, re-securing brackets, clearing downspout clogs (up to $50 value per visit)",
      "Free downspout flushing at every service",
      "15% member discount (upgraded from 10%)",
      "1 free emergency callout per year",
    ],
    popular: true,
  },
  {
    id: "guardian",
    name: "Guardian",
    monthlyPrice: 30,
    yearlyPrice: 360,
    monthlyHref: "https://buy.stripe.com/3cIdRa8NC7Rk5Hd65U2sM08",
    yearlyHref: "https://buy.stripe.com/fZu3cwe7W7Rk8Tp3XM2sM09",
    tagline: "Quarterly visits, unlimited callouts, and full roofline coverage.",
    features: [
      "Everything in Protector, plus:",
      "Quarterly service — 4 visits per year",
      "Free gutter guard cleaning/maintenance for guard-equipped homes",
      "20% member discount",
      "Unlimited emergency callouts with same- or next-day priority in season",
      "Annual roofline & fascia check",
      "Transferable membership if the home is sold",
    ],
    popular: false,
  },
];

export default function Plans() {
  const [billing, setBilling] = useState("monthly"); // "monthly" | "yearly"

  useSeo({
    title: "Membership Plans — Glenwood Gutter Guy",
    description:
      "Maintainer, Protector, and Guardian membership plans for ongoing gutter maintenance in Glenwood and southwest Iowa. Monthly or yearly billing — cancel anytime.",
    image: `${SITE.url}/og-plans.jpg?v=3`,
    url: `${SITE.url}/plans`,
    type: "website",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Top bar */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            data-testid="plans-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="text-xs uppercase tracking-[0.24em] text-white/50">
            Membership Plans
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-10">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
          Ongoing care, no calling around
        </h1>
        <p className="mt-5 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed">
          Pick a plan and Mike comes out on a schedule that matches your home.
          Cancel anytime — billing is handled by Stripe, so you can change or
          pause from your email receipt at any point.
        </p>

        {/* Monthly/Yearly toggle */}
        <div className="mt-8 inline-flex items-center rounded-sm border border-white/10 p-1 bg-white/[0.03]">
          <button
            onClick={() => setBilling("monthly")}
            data-testid="billing-monthly"
            className={`px-5 py-2 text-sm font-bold rounded-sm transition-colors ${
              billing === "monthly"
                ? "bg-[#5FBE1F] text-black"
                : "text-white/60 hover:text-white"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling("yearly")}
            data-testid="billing-yearly"
            className={`px-5 py-2 text-sm font-bold rounded-sm transition-colors ${
              billing === "yearly"
                ? "bg-[#5FBE1F] text-black"
                : "text-white/60 hover:text-white"
            }`}
          >
            Yearly
          </button>
        </div>
        <p className="mt-3 text-xs text-white/40">
          {billing === "yearly"
            ? "One charge, one year of coverage — same monthly rate, just billed upfront."
            : "Billed every month — cancel anytime."}
        </p>
      </section>

      {/* Plan cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((plan) => {
            const href =
              billing === "monthly" ? plan.monthlyHref : plan.yearlyHref;
            const priceDisplay =
              billing === "monthly"
                ? `$${plan.monthlyPrice}`
                : `$${plan.yearlyPrice}`;
            const priceUnit =
              billing === "monthly" ? "/mo" : "/yr";
            const priceSub =
              billing === "monthly" ? null : `$${plan.monthlyPrice}/mo equivalent`;

            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative flex flex-col rounded-sm p-6 sm:p-7 transition-all ${
                  plan.popular
                    ? "border-2 border-[#5FBE1F] bg-[#5FBE1F]/[0.04]"
                    : "border border-white/10 bg-white/[0.02]"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 bg-[#5FBE1F] text-black text-xs font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-sm">
                      <Star className="w-3 h-3" />
                      Most Popular
                    </span>
                  </div>
                )}

                <h2 className="font-display text-3xl text-white">
                  {plan.name}
                </h2>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  {plan.tagline}
                </p>

                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-display text-5xl text-[#5FBE1F]">
                    {priceDisplay}
                  </span>
                  <span className="text-sm text-white/50">{priceUnit}</span>
                </div>
                {priceSub && (
                  <p className="mt-1 text-xs text-white/40">{priceSub}</p>
                )}

                <ul className="mt-6 space-y-3 text-sm text-white/80 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex gap-2.5">
                      <Check className="w-4 h-4 text-[#5FBE1F] shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    track("subscription_click", {
                      tier: plan.id,
                      billing,
                    })
                  }
                  data-testid={`plan-cta-${plan.id}`}
                  className={`mt-7 inline-flex items-center justify-center gap-2 font-bold py-3.5 rounded-sm transition-colors ${
                    plan.popular
                      ? "bg-[#5FBE1F] text-black hover:bg-[#4A9818] hover:text-white"
                      : "bg-white text-black hover:bg-[#5FBE1F]"
                  }`}
                >
                  <Repeat className="w-4 h-4" />
                  Become a {plan.name}
                </a>
              </div>
            );
          })}
        </div>

        {/* Trust footer */}
        <div className="mt-14 border-t border-white/10 pt-8 grid sm:grid-cols-3 gap-6 text-sm text-white/60">
          <div className="flex gap-3">
            <Lock className="w-5 h-5 text-[#5FBE1F] shrink-0 mt-0.5" />
            <div>
              <div className="text-white font-bold mb-1">Cancel anytime</div>
              Manage your subscription directly through the link in your
              Stripe receipt. No phone calls, no contracts.
            </div>
          </div>
          <div className="flex gap-3">
            <Repeat className="w-5 h-5 text-[#5FBE1F] shrink-0 mt-0.5" />
            <div>
              <div className="text-white font-bold mb-1">Built around your home</div>
              Mike will reach out after signup to schedule your first visit
              and lock in your seasonal slot.
            </div>
          </div>
          <div className="flex gap-3">
            <Star className="w-5 h-5 text-[#5FBE1F] shrink-0 mt-0.5" />
            <div>
              <div className="text-white font-bold mb-1">Local & one-man</div>
              Same Mike, same ladder, every visit. No franchise call centers,
              no rotating crews.
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-14">
          <h3 className="font-display text-2xl sm:text-3xl text-white mb-6">
            Common questions
          </h3>
          <dl className="space-y-5 text-sm text-white/70 leading-relaxed">
            <div>
              <dt className="text-white font-bold mb-1">
                What happens after I sign up?
              </dt>
              <dd>
                Stripe sends you a receipt and Mike gets notified. He&apos;ll text
                or call within a day or two to lock in your first visit.
              </dd>
            </div>
            <div>
              <dt className="text-white font-bold mb-1">
                Can I switch tiers later?
              </dt>
              <dd>
                Yes — just text Mike at (712) 310-9514 and he&apos;ll cancel the
                old plan and start you on the new one.
              </dd>
            </div>
            <div>
              <dt className="text-white font-bold mb-1">
                Is the yearly option cheaper?
              </dt>
              <dd>
                Yearly is the same total as paying monthly for a year — just
                one charge instead of twelve. Pick whichever billing rhythm
                fits you.
              </dd>
            </div>
            <div>
              <dt className="text-white font-bold mb-1">
                Do I have to live in Glenwood to join?
              </dt>
              <dd>
                You need to be inside Mike&apos;s service area — Glenwood, Pacific
                Junction, Malvern, Council Bluffs, Red Oak, Shenandoah,
                Bedford, and the surrounding southwest Iowa towns. If your
                town isn&apos;t listed and you&apos;re nearby, text and ask.
              </dd>
            </div>
          </dl>
        </div>

        {/* Help line */}
        <div className="mt-12 text-sm text-white/50">
          Questions about which plan fits your home? Text Mike at{" "}
          <a
            href="sms:+17123109514?body=Plans"
            className="text-[#5FBE1F] underline underline-offset-2 hover:text-white"
            data-testid="plans-sms-help"
            onClick={() =>
              track("text_gutters_click", { location: "plans_page_help" })
            }
          >
            (712) 310-9514
          </a>{" "}
          and he&apos;ll talk it through.
        </div>

        {/* Cross-links: helps SEO + member discovery */}
        <div className="mt-10 pt-8 border-t border-white/10 text-sm text-white/60 flex flex-wrap gap-x-6 gap-y-3">
          <span className="text-white/40">Related:</span>
          <Link
            to="/pay"
            className="text-[#5FBE1F] hover:text-white underline underline-offset-2"
            data-testid="plans-link-pay"
          >
            One-time payment →
          </Link>
          <Link
            to="/member"
            className="text-[#5FBE1F] hover:text-white underline underline-offset-2"
            data-testid="plans-link-member"
          >
            Member portal sign in →
          </Link>
          <Link
            to="/blog"
            className="text-[#5FBE1F] hover:text-white underline underline-offset-2"
            data-testid="plans-link-blog"
          >
            Gutter & home maintenance blog →
          </Link>
        </div>
      </section>
    </div>
  );
}
